import { useContext, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useNavigationManager } from '@provider/navigation';
import { StoreContext, ModalContentManagerProvider } from '@provider/notifier';
import { useObservedValue, useLoaderState, CSSRefTransition } from '@utils/react';
import { DialogContent } from '../components/overlays/dialog.js';
import { dialogSelectors, dialogTransitionDurationMs } from '../components/overlays/dialog.style.js';
import { ModalContent } from '../components/overlays/modal.js';
import { modalSelectors, modalTransitionDurationMs } from '../components/overlays/modal.style.js';
import { backdrop, backdropTransitionDurationMs } from './organism.modal-container.style.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

function ModalContainer() {
  const store = useContext(StoreContext);
  const modals = useObservedValue(store.refs.modals);
  const shareBackdrop = modals.every(modal => modal.shareBackdrop !== false);

  // NB: We've moved the backdrop out to the container. This allows us to
  // maintain the backdrop in flows wherein the UI transitions from one modal to
  // another in rapid succession.
  const showBackdrop = useLoaderState(shareBackdrop && modals.length > 0, {
    delayMs: 0,
    durationMs: 0
  });
  return /*#__PURE__*/jsxs(TransitionGroup, {
    component: null,
    children: [modals.map(modal => /*#__PURE__*/jsx(Modal, {
      modal: modal,
      shareBackdrop: shareBackdrop
    }, modal.id)), showBackdrop.current && /*#__PURE__*/jsx(CSSRefTransition, {
      timeout: backdropTransitionDurationMs,
      classNames: backdrop,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: backdrop
      })
    }, "backdrop")]
  });
}
function Modal({
  modal,
  shareBackdrop,
  ...transitionProps
}) {
  const navigation = useNavigationManager();
  useEffect(() => {
    return navigation.subscribe(() => {
      if (!modal.requiresAction && !modal.persistOnNavigation) {
        modal.onDismiss();
      }
    });
  }, [navigation, modal]);
  return modal.theme === "dialog" ? /*#__PURE__*/jsx(CSSRefTransition, {
    ...transitionProps,
    timeout: dialogTransitionDurationMs,
    classNames: dialogSelectors.wrapper.className,
    children: ref => /*#__PURE__*/jsx(ModalContentManagerProvider, {
      modal: modal,
      children: /*#__PURE__*/jsx(DialogContent, {
        ref: ref,
        className: modal.className,
        onClose: () => modal.onDismiss(),
        noBackdrop: shareBackdrop,
        requiresInteraction: modal.requiresAction,
        includeSpeechButton: modal.includeSpeechButton,
        children: /*#__PURE__*/jsx(Content, {
          modal: modal
        })
      })
    })
  }) : /*#__PURE__*/jsx(CSSRefTransition, {
    ...transitionProps,
    timeout: modalTransitionDurationMs,
    classNames: modalSelectors.wrapper.className,
    children: ref => /*#__PURE__*/jsx(ModalContentManagerProvider, {
      modal: modal,
      children: /*#__PURE__*/jsx(ModalContent, {
        ref: ref,
        className: modal.className,
        onClose: () => modal.onDismiss(),
        theme: modal.theme === "component-framework" ? modal.theme : undefined,
        noBackdrop: shareBackdrop,
        requiresInteraction: modal.requiresAction,
        closeButtonLabel: modal.closeButtonLabel,
        shouldAutoFocusContent: modal.shouldAutoFocusContent,
        includeSpeechButton: modal.includeSpeechButton,
        contentOutsideScrollcontainer: modal.contentOutsideScrollcontainer ? /*#__PURE__*/jsx(ContentOutsideScrollContainer, {
          modal: modal
        }) : undefined,
        children: /*#__PURE__*/jsx(Content, {
          modal: modal
        })
      })
    })
  });
}
function Content(props) {
  return props.modal.content && typeof props.modal.content === "function" ? /*#__PURE__*/jsx(props.modal.content, {}) : /*#__PURE__*/jsx(Fragment, {
    children: props.modal.content
  });
}
function ContentOutsideScrollContainer(props) {
  return props.modal.contentOutsideScrollcontainer && typeof props.modal.contentOutsideScrollcontainer === "function" ? /*#__PURE__*/jsx(props.modal.contentOutsideScrollcontainer, {}) : /*#__PURE__*/jsx(Fragment, {
    children: props.modal.contentOutsideScrollcontainer
  });
}

export { ModalContainer };
//# sourceMappingURL=organism.modal-container.js.map
